import {
  addDays as sharedAddDays,
  DateFormat,
  formatDateToString,
  getLongDateString,
  getTimeOfDayString,
  sameDay,
  toLocalDateTimeInput,
} from '../utils/date';

export { sameDay as isSameDay, sharedAddDays as addDays };

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const HOUR_HEIGHT = 48;
export const SLOT_MINUTES = 30;

export const pad = (n: number): string => String(n).padStart(2, '0');

export const toDateKey = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseApiDate = (s?: string | Date): Date | null => {
  if (!s) return null;
  if (s instanceof Date) return Number.isNaN(s.getTime()) ? null : s;
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fromIsoToDateKey = (iso?: string): string => {
  const d = parseApiDate(iso);
  return d ? toDateKey(d) : '';
};

export const formatApiDateTime = (d: Date): string =>
  formatDateToString(d, DateFormat.API_DATE_TIME_FORMAT);

export const formatDateLong = (d: Date | string): string => getLongDateString(d);

export const formatTime = (iso?: string | Date): string => getTimeOfDayString(iso);

export const roundToQuarter = (d: Date): Date => {
  const next = new Date(d);
  next.setMinutes(Math.round(next.getMinutes() / 15) * 15, 0, 0);
  return next;
};

export const snapToSlot = (d: Date): Date => {
  const next = new Date(d);
  next.setMinutes(Math.floor(next.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES, 0, 0);
  return next;
};

export const dateToLocalInputValue = (d: Date): string => toLocalDateTimeInput(d);

export const localInputValueToDate = (v: string): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const dateToTimeInputValue = (d: Date): string =>
  `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const dayAndTimeToDate = (day: string, time: string): Date | null => {
  if (!day || !time) return null;
  const [y, m, d] = day.split('-').map((p) => Number(p));
  const [hh, mm] = time.split(':').map((p) => Number(p));
  if ([y, m, d, hh, mm].some(Number.isNaN)) return null;
  const out = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(out.getTime()) ? null : out;
};

export const startOfWeek = (d: Date): Date => {
  const next = new Date(d);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - next.getDay());
  return next;
};

export const addMonths = (d: Date, n: number): Date => {
  const next = new Date(d);
  next.setMonth(next.getMonth() + n);
  return next;
};

export const startOfMonth = (year: number, month: number): Date => new Date(year, month, 1);
export const daysInMonth = (year: number, month: number): number =>
  new Date(year, month + 1, 0).getDate();

export const minutesSinceMidnight = (d: Date): number =>
  d.getHours() * 60 + d.getMinutes();

export const pxFromMinutes = (mins: number): number =>
  (mins / 60) * HOUR_HEIGHT;

export const entryCoversDay = (
  entryStart?: string,
  entryEnd?: string,
  day?: Date,
): boolean => {
  if (!entryStart || !entryEnd || !day) return false;
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);
  const s = parseApiDate(entryStart);
  const e = parseApiDate(entryEnd);
  if (!s || !e) return false;
  return s < dayEnd && e > dayStart;
};
